import {
    faArrowRight,
    faDotCircle,
    faDown,
    faDownLeft,
    faDownRight,
    faDrawPolygon,
    faLeft,
    faRight,
    faTrafficLight,
    faUp,
    faUpLeft,
    faUpRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";
import * as turf from "@turf/turf";
import { bearing, point } from "@turf/turf";
import { notification } from "antd";
import CssFilterConverter from "css-filter-converter";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Feature, FeatureCollection } from "geojson";
import mapboxgl from "mapbox-gl";
import {
    MapboxStyleDefinition,
    MapboxStyleSwitcherControl,
    MapboxStyleSwitcherOptions,
} from "mapbox-gl-style-switcher";
import React from "react";
import { Layer } from "react-map-gl/mapbox";
import { globalAppConfig } from "../../config";
import { GlobalStoreContextType } from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import mapIcons from "../../mapIcons";
import { Area } from "../../models/Area";
import { CalendarEvent } from "../../models/CalendarEvent";
import { Delivery } from "../../models/Delivery";
import { Equipment } from "../../models/Equipment";
import { MapObject } from "../../models/MapObject";
import { MapObjectRadius } from "../../models/MapObjectRadius";
import { RepeatingCalendarEventDefinition } from "../../models/RepeatingCalendarEventDefinition";
import { WaypointDirection } from "../../models/WaypointDirection";
import { WaypointStep } from "../../models/WaypointStep";
import PlotAPI from "../../plotAPI";
import TeamInfo from "../../teamInfo";

// dayjs.extend(isSameOrBefore);
// dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
// dayjs.extend(utc);

export function areaOpen(area: MapObject, date: Dayjs | null, mapAreas: Area[], equipment: Equipment[]) {
    if (!date) {
        return true;
    }

    if (!area || !area.mappableId) {
        return false;
    }

    const item = mapAreas.find((item) => item.id === area.mappableId);

    if (!item) {
        return true;
    }

    const areaOpenHoursObject = item.repeatingCalendarEventDefinitions?.find(
        (def: any) => def.template_calendar_event.calendar_event_type === "availability"
    );

    if (areaOpenHoursObject) {
        // Guessing timezone here as we dont actually care about the render just the compare
        const openHours = RepeatingCalendarEventDefinition.fromApiObject(areaOpenHoursObject, dayjs.tz.guess());

        if (openHours.scheduleStart && date.isBefore(openHours.scheduleStart, "day")) {
            return false;
        }

        if (
            openHours.scheduleEnd &&
            (date.isAfter(openHours.scheduleEnd, "day") || date.isSame(openHours.scheduleEnd, "day"))
        ) {
            return false;
        }
    }

    return true;
}

export function equipmentOpen(equipment: Equipment, date: Dayjs | null) {
    if (!date) {
        return true;
    }

    const equipmentOpenHoursObject = equipment.repeatingCalendarEventDefinitions?.find(
        (def: any) => def.template_calendar_event.calendar_event_type === "availability"
    );

    if (equipmentOpenHoursObject) {
        // Guessing timezone here as we dont actually care about the render just the compare
        const openHours = RepeatingCalendarEventDefinition.fromApiObject(equipmentOpenHoursObject, dayjs.tz.guess());

        if (openHours.scheduleStart && date.isBefore(openHours.scheduleStart, "day")) {
            return false;
        }
        if (
            openHours.scheduleEnd &&
            (date.isAfter(openHours.scheduleEnd, "day") || date.isSame(openHours.scheduleEnd, "day"))
        ) {
            return false;
        }
    }

    return true;
}

export function addressFormater(projectInfo) {
    let addressString = "";
    const addressValues = [
        projectInfo.address_1 || projectInfo.address1,
        projectInfo.address_2 || projectInfo.address2,
        projectInfo.city,
        projectInfo.state,
        projectInfo.zip_code || projectInfo.zipCode,
    ];

    addressValues.forEach((val) => {
        if (val) {
            addressString += val;
            if (val === projectInfo.state || val === projectInfo.zip_code) {
                addressString += " ";
            } else {
                addressString += ", ";
            }
        }
    });

    return addressString;
}

export function iconType(objectType: string) {
    if (objectType === "point") {
        return faDotCircle;
    } else if (objectType === "polygon") {
        return faDrawPolygon;
    } else {
        return faArrowRight;
    }
}

export function areaAvatar(
    area: Area | Equipment | null,
    mapObject: MapObject | null | undefined,
    size: string = "1rem"
) {
    if (!mapObject || !area) {
        return null;
    }
    let color = mapObject.color || "#ff9e00";
    if (mapObject.geometry === "lineString") {
        color = mapObject.outline || mapObject.color || "#ff9e00";
    }

    if (mapObject.icon) {
        const style = {
            width: size,
            filter: mapObject.icon?.includes("sdf") ? CssFilterConverter.hexToFilter(color).color : null,
        };
        return <img style={style} src={`/img/png/map/${mapObject.icon}.png`} className="mr-2" />;
    }

    return (
        <FontAwesomeIcon
            className="mr-2"
            icon={iconType(mapObject.geometry)}
            color={color}
            style={{ width: size, height: size }}
        />
    );
}

export function equipmentAvatar(mapObject: MapObject, size: string = "1rem") {
    if (mapObject.icon) {
        const style = {
            width: size,
            filter: mapObject.icon?.includes("sdf")
                ? CssFilterConverter.hexToFilter(mapObject.color || "#ff9e00").color
                : null,
        };
        return <img style={style} src={`/img/png/map/${mapObject.icon}.png`} className="mr-2" />;
    }

    return (
        <FontAwesomeIcon
            icon={iconType(mapObject.geometry)}
            color={mapObject.color || "#ff9e00"}
            width={size}
            className="mr-2"
        />
    );
}

export function geocoderHelperResultFormatter(result) {
    result.context.push({
        id: result.id,
        text: result.text,
    });
    const location = {
        name: null,
        address: null,
        city: null,
        zip: null,
        state: null,
        country: null,
        lon: result.geometry.coordinates[0],
        lat: result.geometry.coordinates[1],
    };
    if (result.place_type.includes("address")) {
        location.address = result.place_name.substring(0, result.place_name.indexOf(","));
    }
    if (result.place_type.includes("poi")) {
        location.address = result.properties.address;
        location.name = result.place_name.substring(0, result.place_name.indexOf(","));
    }
    location.city = result.context.find((x) => {
        return x.id.startsWith("place");
    }).text;
    if (
        result.context.find((x) => {
            return x.id.startsWith("postcode");
        })
    ) {
        location.zip = result.context.find((x) => {
            return x.id.startsWith("postcode");
        }).text;
    }
    location.state = result.context.find((x) => {
        return x.id.startsWith("region");
    }).text;
    location.country = result.context.find((x) => {
        return x.id.startsWith("country");
    }).text;
    return location;
}

export function validateZipCode(val) {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val);
}

export function findLayerIndex(array, val) {
    return array.findIndex((layer) => layer.features.find((feature) => feature.id === val));
}

export function isMobileDevice() {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return true;
    } else {
        return false;
    }
}

export function openMapLink(endPoint: MapObject) {
    if (!endPoint) {
        return;
    }

    let destination = "";

    if (endPoint.geometry === "lineString") {
        const coordString = `${endPoint.coordinates[0][1]}, ${endPoint.coordinates[0][0]}`;
        destination = coordString;
    } else {
        const center = endPoint.getCenter();
        destination = `${center[1]},${center[0]}`;
    }

    const url = new URL("https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=My+Location");
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS && destination) {
        url.protocol = "maps:";
        url.host = "maps.google.com";
    }

    url.searchParams.append("destination", destination);

    window.open(url.toString());
}

export const getMapObjectCenter = (object: MapObject) => {
    const points = turf.points(object.coordinates);

    return turf.center(points);
};

export const getMapMaxMetersPerPixelAtZoom = (map: mapboxgl.Map) => {
    if (!map) {
        return 1; // Default to 1 meter per pixel if map is not available
    }
    const y = map.getCanvas()?.height;
    const x = map.getCanvas()?.width;

    const left = map.unproject([0, y]);
    const bottom = map.unproject([x, y]);
    const maxMeters =
        turf.distance(turf.point([left.lng, left.lat]), turf.point([bottom.lng, bottom.lat]), {
            units: "miles",
        }) * 1609.344;
    const metersPerPixel = maxMeters / x;
    return metersPerPixel;
};

export const getIconRealWorldSize = (mapObject: MapObject, metersPerPixel: number) => {
    let size = mapObject.iconWidth || 1;
    if (mapObject.iconUnits !== "meters") {
        size = size * 0.3048;
    }
    const iconPixels = size / metersPerPixel;
    const iconSize = iconPixels / 128; // 128 is the default size of the icon in pixels. All icons are 128x128
    if (iconSize && iconSize !== undefined && iconSize !== Infinity) {
        return iconSize;
    } else {
        return 1;
    }
};

export const defaultIconSizeArray = (iconType: boolean): any[] => {
    const svgIconSizeArray = ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 0.1, 14, 0.5, 16, 1, 17, 0.5];
    const iconSizeArray = ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 0.1, 14, 0.2, 16, 0.25];

    if (iconType) {
        return iconSizeArray;
    }
    return svgIconSizeArray;
};

export const mapStyles: MapboxStyleDefinition[] = [
    {
        title: "Satellite-ESRI",
        uri: globalAppConfig.ARC_GIS_IMAGERY_URL,
    },
    // Breaks when using react-map-gl
    // {
    //     title: "Satellite-Mapbox",
    //     uri: `mapbox://styles/mapbox/standard-satellite`,
    // },
    {
        title: "Streets",
        uri: "mapbox://styles/mapbox/streets-v12",
    },
    {
        title: "Satellite Streets",
        uri: "mapbox://styles/mapbox/satellite-streets-v12",
    },
    {
        title: "Dark",
        uri: "mapbox://styles/mapbox/dark-v11",
    },
    {
        title: "Light",
        uri: "mapbox://styles/mapbox/light-v11",
    },
    {
        title: "Outdoors",
        uri: "mapbox://styles/mapbox/outdoors-v12",
    },
];

export const addMapControls = (map: mapboxgl.Map, mapStyleOptions: MapboxStyleSwitcherOptions) => {
    if (!map) {
        return;
    }
    //top-left controls

    // top-right controls
    map.addControl(new mapboxgl.GeolocateControl(), "top-right");
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.addControl(new mapboxgl.FullscreenControl(), "top-right");
    map.addControl(new MapboxStyleSwitcherControl(mapStyles, mapStyleOptions), "top-right");

    // bottom-right controls
    map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
};

// TODO: This is being added to try and help debug `addToBounds` below.
const extractLngLatPairs = (input: any): [number, number][] => {
    const result: [number, number][] = [];
    const invalidItems: any[] = [];

    const recurse = (item: any) => {
        if (Array.isArray(item) && item.length === 2 && typeof item[0] === "number" && typeof item[1] === "number") {
            result.push(item as [number, number]);
        } else if (Array.isArray(item)) {
            item.forEach(recurse);
        } else {
            invalidItems.push(item);
        }
    };

    recurse(input);

    if (invalidItems.length > 0) {
        Sentry.captureMessage("Non-lng/lat items encountered in extractLngLatPairs", {
            level: "warning",
            extra: {
                invalidItems,
                originalInput: input,
            },
        });
    }

    return result;
};

export const addToBounds = (object: MapObject, currentBounds: mapboxgl.LngLatBounds): mapboxgl.LngLatBounds => {
    try {
        const bounds = currentBounds || new mapboxgl.LngLatBounds();

        const lngLatPairs = extractLngLatPairs(object.coordinates);
        lngLatPairs.forEach((pair) => bounds.extend(pair));

        return bounds;
    } catch (error) {
        Sentry.captureException(error, {
            extra: {
                object,
                currentBounds,
            },
        });
        return new mapboxgl.LngLatBounds();
    }
};

export const generateMapGeoJson = (mapObjects: MapObject[]): FeatureCollection => {
    const geoJsonData: { type: string; features: any[] } = {
        type: "FeatureCollection",
        features: [],
    };

    mapObjects
        .filter((mapObject: MapObject) => mapObject instanceof MapObject)
        .forEach((mapObject: MapObject) => {
            geoJsonData.features.push(mapObject.toFeature());
            if (mapObject.geometry == "lineString") {
                geoJsonData.features = geoJsonData.features.concat(arrowCapSourceBuilder(mapObject));
            }
            if (mapObject.radiuses && mapObject.radiuses.length > 0) {
                mapObject.radiuses.forEach((radius) => {
                    geoJsonData.features.push(radius.toFeature(mapObject));
                });
            }
        });

    return geoJsonData as FeatureCollection;
};

export const addGeoJsonSourceToMap = (
    map: mapboxgl.Map,
    sourceId: string,
    mapObjects: MapObject[],
    context: GlobalStoreContextType
) => {
    if (map.getSource(sourceId)) {
        return;
    }
    const data = generateMapGeoJson(mapObjects);

    return map.addSource(sourceId, {
        type: "geojson",
        data: data,
        // Cannot use dynamic here because it causes issues with rendering polygons
        // dynamic: true,
    });
};

export const addIcon = (
    object: MapObject,
    sourceId: string,
    map: mapboxgl.Map,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <>
            {object.radiuses && object.radiuses.length > 0 && (
                <React.Fragment>
                    {object.radiuses.map((radius) => {
                        return (
                            <React.Fragment key={`${object.uuid}_radius_${radius.id}`}>
                                <Layer
                                    key={`${object.uuid}_radius_${radius.id}`}
                                    id={`${object.uuid}_radius_${radius.id}`}
                                    slot="top"
                                    source={sourceId}
                                    type="fill"
                                    paint={{
                                        "fill-opacity": radius.opacity || 1,
                                        "fill-color": radius.color || "#ff9e00",
                                    }}
                                    layout={{
                                        visibility: visibility,
                                    }}
                                    filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                />

                                <Layer
                                    key={`${object.uuid}_radius_border_${radius.id}`}
                                    id={`${object.uuid}_radius_border_${radius.id}`}
                                    slot="top"
                                    source={sourceId}
                                    type="line"
                                    layout={{
                                        visibility: radius.showBorder ? visibility : "none",
                                    }}
                                    paint={{
                                        "line-color": radius.color || "#ff9e00",
                                        "line-width": 2,
                                        "line-opacity": 1,
                                    }}
                                    filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                />

                                {radius.name &&
                                    map &&
                                    map.isStyleLoaded() &&
                                    !map?.getStyle()?.sprite?.includes("arcgis") && (
                                        <Layer
                                            key={`${object.uuid}_radius_label_${radius.id}`}
                                            id={`${object.uuid}_radius_label_${radius.id}`}
                                            slot="top"
                                            source={sourceId}
                                            type="symbol"
                                            layout={{
                                                visibility: visibility,
                                                "text-field": radius.name,
                                                "symbol-placement": "line",

                                                "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                                                "text-size": 12,
                                                "text-anchor": "center",
                                                "text-allow-overlap": true,
                                            }}
                                            paint={{
                                                "text-color": "#FFFFFF",
                                                "text-halo-color": "#222222",
                                                "text-halo-width": 1,
                                            }}
                                            filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                        />
                                    )}
                            </React.Fragment>
                        );
                    })}
                </React.Fragment>
            )}
            <Layer
                id={object.uuid}
                slot="top"
                source={sourceId}
                type="symbol"
                layout={{
                    visibility: visibility,
                    "icon-image": object.icon || "bullet_sdf_icon",
                    "icon-size": iconSize,
                    "icon-anchor": object.iconAnchor ? object.mapboxIconAnchor() : "center",
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                }}
                paint={{
                    "icon-color": object.color || "#ff9e00",
                    "icon-opacity": object.opacity || 1,
                    "icon-translate-transition": { duration: 2000 },
                    "icon-halo-width-transition": { duration: 2000 },
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
        </>
    );
};

export const addLine = (
    object: MapObject,
    sourceId: string,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <React.Fragment key={object.uuid}>
            <Layer
                id={object.uuid}
                slot="top"
                source={sourceId}
                type="line"
                paint={{
                    "line-color": object.outline || object.color || "#ff9e00",
                    "line-width": 5,
                    "line-opacity": object.opacity ? object.opacity : 1.0,
                }}
                layout={{
                    visibility: visibility,
                    "line-join": "round",
                    "line-cap": "round",
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
            {object.icon && (
                <React.Fragment>
                    <Layer
                        key={`${object.uuid}_line_icon`}
                        id={`${object.uuid}_line_icon`}
                        slot="top"
                        source={sourceId}
                        type="symbol"
                        layout={{
                            visibility: visibility,
                            "symbol-placement": "line",
                            "symbol-spacing": 1,
                            "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
                            "icon-size": iconSize,
                            "icon-allow-overlap": true,
                            "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
                        }}
                        paint={{
                            "icon-color": object.color || object.outline || "#ff9e00",
                        }}
                        filter={["==", ["get", "id"], object.uuid]}
                    />
                    <Layer
                        key={`${object.uuid}_lineCaps`}
                        id={`${object.uuid}_lineCaps`}
                        slot="top"
                        source={sourceId}
                        type="symbol"
                        layout={{
                            visibility: visibility,
                            "icon-image": object.icon || iconImage(object),
                            "icon-size": iconSize,
                            "icon-rotate": ["get", "rotation"],
                            "icon-anchor": "center",
                            "icon-rotation-alignment": "map",
                            "icon-allow-overlap": true,
                            "icon-ignore-placement": true,
                        }}
                        paint={{
                            "icon-color": object.color || object.outline || "#ff9e00",
                        }}
                        filter={[
                            "in",
                            ["get", "id"],
                            ["literal", [`${object.uuid}_startCap`, `${object.uuid}_endCap`]],
                        ]}
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export const addPolygon = (
    object: MapObject,
    sourceId: string,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <React.Fragment key={object.uuid}>
            <Layer
                key={object.uuid}
                id={object.uuid}
                slot="top"
                source={sourceId}
                type="fill"
                paint={{
                    "fill-color": object.color || "#ff9e00",
                    "fill-opacity": object.opacity || 0.75,
                }}
                layout={{
                    visibility: visibility,
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />

            <Layer
                key={`${object.uuid}_outline`}
                id={`${object.uuid}_outline`}
                slot="top"
                source={sourceId}
                type="line"
                paint={{
                    "line-color": object.outline || object.color || "#ff9e00",
                    "line-width": 2,
                    "line-dasharray": [10],
                }}
                layout={{
                    visibility: visibility,
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
            {object.icon && (
                <Layer
                    key={`${object.uuid}_outline_icon`}
                    id={`${object.uuid}_outline_icon`}
                    slot="top"
                    source={sourceId}
                    type="symbol"
                    layout={{
                        visibility: visibility,
                        "symbol-placement": "line",
                        "symbol-spacing": 1,
                        "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
                        "icon-size": iconSize,
                        // ["interpolate", ["linear"], ["zoom"], 10, 0.7, 15, 0.3],
                        "icon-allow-overlap": true,
                        "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
                    }}
                    paint={{
                        "icon-color": object.outline || object.color || "#ff9e00",
                    }}
                    filter={["==", ["get", "id"], object.uuid]}
                />
            )}
        </React.Fragment>
    );
};

export const addPolygonIconBorder = async (
    map: mapboxgl.Map,
    object: MapObject,
    layerId: string,
    visibility: "visible" | "none",
    iconSize = 1
) => {
    return map.addLayer({
        id: `${object.uuid}_outline_icon`,
        slot: "top",
        type: "symbol",
        source: layerId,
        layout: {
            visibility: visibility,
            "symbol-placement": "line",
            "symbol-spacing": 1,
            "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
            "icon-size": iconSize,
            // ["interpolate", ["linear"], ["zoom"], 10, 0.7, 15, 0.3],
            "icon-allow-overlap": true,
            "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
        },
        paint: {
            "icon-color": object.outline || object.color || "#ff9e00",
        },

        filter: ["==", ["get", "id"], object.uuid],
    });
};

const arrowCapSourceBuilder = (object: MapObject): Feature[] => {
    return [
        {
            id: `${object.uuid}_startCap`,
            type: "Feature",
            properties: {
                id: `${object.uuid}_startCap`,
                rotation:
                    object.icon && object.icon.includes("no-entry")
                        ? getStartCapBearing(object) + 90 + 90
                        : getStartCapBearing(object) + 90,
            },
            geometry: {
                type: "Point",
                coordinates: object.coordinates[0],
            },
        },
        {
            id: `${object.uuid}_endCap`,
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: object.coordinates.at(-1),
            },
            properties: {
                id: `${object.uuid}_startCap`,
                rotation:
                    object.icon && object.icon.includes("no-entry")
                        ? getEndCapBearing(object) + 270 + 90
                        : getEndCapBearing(object) + 270,
            },
        },
    ];
};

const getEndCapBearing = (object: MapObject) => {
    if (object.coordinates?.length < 2) {
        return 0; // Not enough points to calculate bearing
    }
    const point1 = point(object.coordinates[object.coordinates.length - 2]);
    const point2 = point(object.coordinates[object.coordinates.length - 1]);
    const mapBearing = bearing(point1, point2);
    return mapBearing;
};
const getStartCapBearing = (object: MapObject) => {
    if (object.coordinates?.length < 2) {
        return 0; // Not enough points to calculate bearing
    }
    const point1 = point(object.coordinates[1]);
    const point2 = point(object.coordinates[0]);
    const mapBearing = bearing(point1, point2);
    return mapBearing;
};
const iconImage = (object: any) => {
    if (object.icon) {
        return object.icon;
    }
    return "bullet_sdf_icon";
};

export const addMarkerImages = async (map: mapboxgl.Map) => {
    const flatMap = [
        ...mapIcons.equipmentIcons,
        ...mapIcons.sdfIcons,
        ...mapIcons.letterIcons,
        ...mapIcons.numberIcons,
    ];

    return Promise.all(
        flatMap
            .filter((icon) => icon !== undefined && icon !== "")
            .map((icon) => {
                return new Promise<void>((resolve, reject) => {
                    if (map.hasImage(icon)) {
                        return resolve();
                    }
                    map.loadImage(`/img/png/map/${icon}.png`, (_error, image) => {
                        if (!image) {
                            return resolve();
                        }
                        // Add the image to the map style.
                        map.addImage(icon, image, { sdf: icon.includes("sdf") ? true : false });
                        return resolve();
                    });
                });
            })
    );
};

export const generateInteractiveLayerIds = (mapObjects: MapObject[]) => {
    const interactiveLayerIds: string[] = [];

    mapObjects.forEach((mapObject) => {
        interactiveLayerIds.push(mapObject.uuid);
        if (mapObject.geometry === "lineString" && mapObject.icon) {
            interactiveLayerIds.push(`${mapObject.uuid}_line_icon`);
            interactiveLayerIds.push(`${mapObject.uuid}_lineCaps`);
        }
        if (mapObject.geometry === "polygon") {
            interactiveLayerIds.push(`${mapObject.uuid}_outline`);
            if (mapObject.icon) {
                interactiveLayerIds.push(`${mapObject.uuid}_outline_icon`);
            }
        }
        if (mapObject.radiuses && mapObject.radiuses.length > 0) {
            mapObject.radiuses.forEach((radius) => {
                interactiveLayerIds.push(`${mapObject.uuid}_radius_${radius.id}`);
                if (radius.showBorder) {
                    interactiveLayerIds.push(`${mapObject.uuid}_radius_border_${radius.id}`);
                }
                if (radius.name) {
                    interactiveLayerIds.push(`${mapObject.uuid}_radius_label_${radius.id}`);
                }
            });
        }
    });

    return interactiveLayerIds;
};

export const addNecessaryMarkerImages = async (icons: string[], map: mapboxgl.Map) => {
    return Promise.all(
        icons
            .filter((icon) => icon !== undefined && icon !== "")
            .map((icon) => {
                return new Promise<void>((resolve, reject) => {
                    map.loadImage(`/img/png/map/${icon}.png`, (error, image) => {
                        if (error) {
                            reject(error);
                            return;
                        }
                        if (!image) {
                            resolve();
                            return;
                        }
                        // Add the image to the map style.
                        if (!map.hasImage(icon)) {
                            map.addImage(icon, image, { sdf: icon.includes("sdf") ? true : false });
                        }
                        resolve();
                    });
                });
            })
    );
};

export const toggleShowOpenLocations = (
    map: mapboxgl.Map,
    context: GlobalStoreContextType,
    mapObjects: MapObject[],
    date: Dayjs = dayjs()
) => {
    mapObjects.forEach((mapObject) => {
        const mappable = mapObject.getMappable(context);
        if (!mappable) {
            return;
        }

        let visibility: "visible" | "none" = "none";

        if (
            mappable instanceof Area &&
            areaOpen(
                mapObject,
                date,
                context.mapAreasForProject(context.currentProjectInfo.unique_token),
                context.equipmentForProject(context.currentProjectInfo.unique_token)
            )
        ) {
            visibility = "visible";
        } else if (mappable instanceof Equipment && equipmentOpen(mappable, date)) {
            visibility = "visible";
        }

        mapObject.setVisibility(map, visibility);
    });
};

export const deliveryMapToggleDisplayLogic = (
    mapRef: mapboxgl.Map,
    context: GlobalStoreContextType,
    date: Dayjs,
    mapObjects: MapObject[] = []
) => {
    if (!mapRef) {
        return;
    }

    const eventsForDay = context.calendarEventsByProject
        .flatMap((project) => project.calendarEvents)
        .filter((event) => event.calendarableType === "Delivery" && event.startAt.isSame(date, "day"));

    mapObjects.forEach((mapObject) => {
        const mappable = mapObject.getMappable(context);
        if (!mappable) {
            return;
        }
        let visibility: "visible" | "none" = "none";
        if (calendarShowArea(context, mappable, eventsForDay)) {
            visibility = "visible";
        }
        mapObject.setVisibility(mapRef, visibility);
    });
};

export const calendarShowArea = (
    context: GlobalStoreContextType,
    mappable: Area | Equipment,
    eventsForDay: CalendarEvent[]
) => {
    if (
        mappable instanceof Area &&
        context.calendarEventsFilterInfo.calendarEventResources.find(
            (resource) =>
                resource.calendarResourceableType === "Area" && resource.calendarResourceableId === mappable.id
        )
    ) {
        return true;
    }

    if (
        mappable instanceof Equipment &&
        context.calendarEventsFilterInfo.calendarEventResources.find(
            (resource) =>
                resource.calendarResourceableType === "Equipment" && resource.calendarResourceableId === mappable.id
        )
    ) {
        return true;
    }

    if (mappable instanceof Area && eventsForDay.some((event) => event.deliveryDropOffId() === mappable.id)) {
        return true;
    }

    if (
        mappable instanceof Equipment &&
        eventsForDay.some((event) => event.deliveryEquipmentIds().some((id) => id === mappable.id))
    ) {
        return true;
    }

    return false;
};
type mapWeatherEndpoints =
    | "temperature"
    | "dewPoint"
    | "humidity"
    | "windSpeed"
    | "windDirection"
    | "precipitationIntensity"
    | "visibility"
    | "lightning";
export const addRadar = (
    map: mapboxgl.Map,
    context: GlobalStoreContextType,
    endpoint: mapWeatherEndpoints = "precipitationIntensity"
) => {
    if (!map) {
        return;
    }

    const jobsiteEntrance = context
        .mapAreasForProject(context.currentProjectInfo.unique_token)
        .find((area) => area.areaName === "Jobsite Entrance");

    map.setZoom(map.getZoom() > 6 ? 6 : map.getZoom());
};

export const removeRadar = (map: mapboxgl.Map, context: GlobalStoreContextType) => {
    if (map.getLayer("radar-tiles")) {
        map.removeLayer("radar-tiles");
    }
    if (map.getSource("radar-tiles")) {
        map.removeSource("radar-tiles");
    }

    map.flyTo({ center: map.getCenter(), zoom: context.currentProjectInfo.zoom || 16 });
};

export const openEditAreaMapHandler = (mappable: Area | Equipment, context: GlobalStoreContextType) => {
    const mapObject = mappable.getMapObject(context);
    if (!mapObject) {
        return;
    }
    context.setEditCurrentMappable(true);

    if (!context.mapRef?.current) {
        return;
    }

    const map = context.mapRef.current.getMap();

    if (!map) return;

    context.mapBoxDraw.add(mapObject.toFeature());
    if (mapObject?.geometry !== "point") {
        context.mapBoxDraw.changeMode("direct_select", { featureId: mapObject?.uuid });
    }
    map.flyTo({
        center: mapObject?.getCenter(),
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
    });
    // Mapbox draw doesn't put the draw layers on top of other layers by default, so we need to move them to the top
    const mapDrawLayers = map.getStyle().layers?.filter((layer) => layer.id.startsWith("gl-draw"));

    mapDrawLayers?.forEach((layer) => {
        map.moveLayer(layer.id);
    });
};

export const archiveMapObjectRemoveLayers = (
    context: GlobalStoreContextType,
    map: mapboxgl.Map,
    mapObject: MapObject
) => {
    mapObject.removeLayerFromMapbox(map);
    context.setNewMappableType(null);
};

export const addGeotiff = (map: mapboxgl.Map, geotiff: any) => {
    if (!map || !geotiff) {
        return;
    }

    if (map.getSource(`${geotiff.id}_geotiff`) || map.getLayer(`${geotiff.id}_geotiff`)) {
        return;
    }

    const geotiffUrl = `mapbox://nickdreiling.${geotiff.geotiff_name}`;
    map.addSource(`${geotiff.id}_geotiff`, {
        type: "raster",
        url: geotiffUrl,
    }).addLayer({
        id: `${geotiff.id}_geotiff`,
        type: "raster",
        source: `${geotiff.id}_geotiff`,
    });
};

export const setMapView = (map: mapboxgl.Map | null | undefined, projectInfo: any) => {
    if (!map) {
        return;
    }

    const center = map.getCenter();
    const zoom = map.getZoom();
    const style = map.getStyle().sprite?.includes("arcgis")
        ? globalAppConfig.ARC_GIS_IMAGERY_URL
        : map.getStyle().sprite;
    const newProjectInfo = projectInfo;
    newProjectInfo.lat_override = center.lat;
    newProjectInfo.lon_override = center.lng;
    newProjectInfo.map_zoom = zoom;
    newProjectInfo.map_style = style;

    PlotAPI.putProjectInfo(newProjectInfo.unique_token, newProjectInfo).then((response) => {
        if (response) {
            notification.success({
                message: "Default Map View Updated",
                description: "The map view has been saved to your jobsite successfully.",
            });
            return { success: true, message: "Default Map View Updated" };
        } else {
            notification.error({
                message: "Failed to Update Map View",
                description: "There was an issue saving the map view. Please try again.",
            });
            return { success: false, message: "Failed to Update Map View" };
        }
    });
};
export const updateMapIconSize = (map: mapboxgl.Map, iconSize: number, mapObject: MapObject) => {
    if (!map || !mapObject) {
        return;
    }
    let layerId = mapObject.uuid;

    if (mapObject.geometry == "polygon") {
        layerId = `${mapObject.uuid}_outline_icon`;
    }
    if (mapObject.geometry == "lineString") {
        layerId = `${mapObject.uuid}_line_icon}`;
    }

    if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "icon-size", iconSize);
    }

    if (map.getLayer(`${mapObject.uuid}_lineCaps`)) {
        map.setLayoutProperty(`${mapObject.uuid}_lineCaps`, "icon-size", iconSize);
    }
};

const updateWaypointDirection = (
    context: GlobalStoreContextType,
    area: Area,
    mapObject: MapObject,
    newCoordinates: [number, number][] | [number, number]
) => {
    const waypointDirection = area.waypointDirection;

    if (!waypointDirection || !waypointDirection.waypointSteps || waypointDirection.waypointSteps.length === 0) {
        return;
    }

    // If this waypoint is a point it is not an array of coordinates so we need to handle that separately
    if (mapObject.geometry === "point") {
        updateWaypointPoint(context, area, newCoordinates as [number, number]);
        return;
    }

    if (waypointDirection.waypointSteps.length === newCoordinates.length) {
        updateWaypointStepsCoordinates(context, area, newCoordinates);
        return;
    }

    if (waypointDirection.waypointSteps.length < newCoordinates.length) {
        addWaypointStep(context, area, mapObject, newCoordinates);
        return;
    }

    if (waypointDirection.waypointSteps.length > newCoordinates.length) {
        deleteWaypointStep(context, area, newCoordinates);
        return;
    }
};

const addWaypointStep = (
    context: GlobalStoreContextType,
    area: Area,
    mapObject: MapObject,
    newCoordinates: [number, number][]
) => {
    const waypointDirection = area.waypointDirection;
    if (!waypointDirection?.waypointSteps) {
        return;
    }

    const newStepIndex = newCoordinates.findIndex(
        (nc) => !waypointDirection.waypointSteps.find((ws) => ws.coordinates[0] == nc[0] && ws.coordinates[1] == nc[1])
    );
    const newStep = WaypointStep.fromApiObject(
        {
            step_number: newStepIndex + 1,
            coordinates: newCoordinates[newStepIndex],
            icon: waypointDirection.waypointSteps[newStepIndex - 1].icon,
            instruction: `Proceed to waypoint step ${newStepIndex + 1}`,
            color: waypointDirection.waypointSteps[newStepIndex - 1].color,
            icon_rotate: 0,
        },
        context.currentProjectInfo.timezone
    );

    waypointDirection.waypointSteps.splice(newStepIndex, 0, newStep);

    // Update step numbers for subsequent steps
    waypointDirection.waypointSteps.forEach((step, idx) => {
        if (idx >= newStepIndex) {
            step.stepNumber = idx + 1;
        }
    });
    const tempArea = Object.create(area) as Area;
    tempArea.waypointDirection = waypointDirection;
    context.setCurrentMappable(tempArea);
};

const updateWaypointPoint = (context: GlobalStoreContextType, area: Area, newCoordinate: [number, number]) => {
    const tempArea = Object.create(area) as Area;
    if (!tempArea.waypointDirection) {
        return;
    }
    const tempWaypointDirection = Object.create(tempArea.waypointDirection) as WaypointDirection;
    if (!tempWaypointDirection?.waypointSteps || tempWaypointDirection.waypointSteps.length === 0) {
        return;
    }

    tempWaypointDirection.waypointSteps = tempWaypointDirection.waypointSteps.map((step) => {
        const tempStep = Object.create(step) as WaypointStep;
        tempStep.coordinates = newCoordinate;
        return tempStep;
    });

    tempArea.waypointDirection = tempWaypointDirection;

    context.setCurrentMappable(tempArea);
};

const updateWaypointStepsCoordinates = (
    context: GlobalStoreContextType,
    area: Area,
    newCoordinates: [number, number][]
) => {
    const tempArea = Object.create(area) as Area;
    if (!tempArea.waypointDirection) {
        return;
    }
    const tempWaypointDirection = Object.create(tempArea.waypointDirection) as WaypointDirection;
    if (
        !tempArea.waypointDirection ||
        !tempWaypointDirection?.waypointSteps ||
        tempWaypointDirection.waypointSteps.length === 0
    ) {
        return;
    }

    tempWaypointDirection.waypointSteps = tempWaypointDirection.waypointSteps.map((step, index) => {
        const tempStep = Object.create(step) as WaypointStep;
        tempStep.coordinates = newCoordinates[index];
        return tempStep;
    });

    tempArea.waypointDirection = tempWaypointDirection;

    context.setCurrentMappable(tempArea);
};

const deleteWaypointStep = (context: GlobalStoreContextType, area: Area, newCoordinates: [number, number][]) => {
    if (!area.waypointDirection) {
        return;
    }
    const waypointDirection = Object.create(area.waypointDirection) as WaypointDirection;
    if (!waypointDirection?.waypointSteps) {
        return;
    }

    const stepIndexToDelete = waypointDirection.waypointSteps.findIndex(
        (ws) => !newCoordinates.find((nc) => ws.coordinates[0] == nc[0] && ws.coordinates[1] == nc[1])
    );

    if (stepIndexToDelete === -1) {
        return;
    }

    // waypointDirection.waypointSteps.splice(stepIndexToDelete, 1);
    waypointDirection.waypointSteps[stepIndexToDelete]._destroy = true;

    // Update step numbers for subsequent steps
    waypointDirection.waypointSteps
        .filter((step) => !step._destroy)
        .forEach((step, idx) => {
            if (idx >= stepIndexToDelete) {
                step.stepNumber = idx + 1;
            }
        });
    const tempArea = Object.create(area) as Area;
    tempArea.waypointDirection = waypointDirection;
    context.setCurrentMappable(tempArea);
};

export const onDrawUpdate = (e: mapboxgl.MapMouseEvent & { features: any[] }, context: GlobalStoreContextType) => {
    const newCurrentMapObject = Object.create(context.currentMapObject) as MapObject;

    if (context.currentMappable instanceof Area && context.currentMappable.waypointDirection) {
        updateWaypointDirection(
            context,
            context.currentMappable,
            newCurrentMapObject,
            e.features[0].geometry.coordinates
        );
    }

    newCurrentMapObject.coordinates = e.features[0].geometry.coordinates;
    context.setCurrentMapObject(newCurrentMapObject);
    const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token).map((x) => {
        return Object.create(x) as MapObject;
    });
    if (projectMapObjects.find((x) => x.uuid === newCurrentMapObject.uuid)) {
        projectMapObjects.find((x) => x.uuid === newCurrentMapObject.uuid)!.coordinates =
            newCurrentMapObject.coordinates;
    } else {
        projectMapObjects.push(newCurrentMapObject);
    }
    context.setMapGeoJson(generateMapGeoJson(projectMapObjects));
};

export const onDrawCreate = (e: mapboxgl.MapMouseEvent & { features: any[] }, context: GlobalStoreContextType) => {
    const newMapObject = MapObject.fromFeature(e.features[0], context.currentProjectInfo, context.newMappableType);

    context.setCurrentMapObject(newMapObject);

    const newMappable =
        context.newMappableType == "equipment"
            ? Equipment.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone)
            : Area.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone);

    context.setCurrentMappable(newMappable);

    context.setEditCurrentMappable(true);
    context.setEditCurrentMappablePosition(true);

    const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token);
    if (!projectMapObjects.find((x) => x.uuid === newMapObject.uuid)) {
        projectMapObjects.push(newMapObject);
    }
    context.setMapGeoJson(generateMapGeoJson(projectMapObjects));
    return newMapObject;
};

export const updateRadiusSize = (
    mapObject: MapObject,
    radius: MapObjectRadius,
    distance: number,
    units: "feet" | "meters" = "feet",
    index: number,
    context: GlobalStoreContextType
) => {
    if (!mapObject) {
        return;
    }
    const newCurrentMapObject = Object.create(mapObject) as MapObject;
    const newRadiuses = (newCurrentMapObject.radiuses || []).map((o) => Object.create(o) as MapObjectRadius);
    newRadiuses[index].radius = distance;
    newRadiuses[index].units = units;
    if (!newCurrentMapObject.radiuses) {
        newCurrentMapObject.radiuses = [newRadiuses[index]];
    } else {
        newCurrentMapObject.radiuses[index] = newRadiuses[index];
    }
    const radiusId = radius.id || index;
    const radiusFeatureIndex = context.mapGeoJson?.features.findIndex(
        (f) => f.id === `${newCurrentMapObject.uuid}_radius_${radiusId}`
    );
    context.setCurrentMapObject(newCurrentMapObject);

    const center = newCurrentMapObject.coordinates;
    const options = { units: units, steps: 10, properties: { id: `${newCurrentMapObject.uuid}_radius_${radiusId}` } };
    const diameter = turf.circle(center, distance, options) as GeoJSON.Feature;

    const tempGeoJson = { ...context.mapGeoJson } as GeoJSON.FeatureCollection;

    if (radiusFeatureIndex === -1) {
        // Feature not found; optionally log or handle this case
        return;
    }
    tempGeoJson.features[radiusFeatureIndex].geometry = diameter.geometry;
    context.setMapGeoJson(tempGeoJson);
};

export const randomColorGenerator = () => {
    return "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0");
};

export const resetMapObject = (context: GlobalStoreContextType, mapObject: MapObject) => {
    const mappable = mapObject.getMappable(context);
    if (!mappable) {
        return;
    }
    const originalMapObject = mappable.getMapObject(context);
    if (!originalMapObject) {
        return;
    }
    const map = context.mapRef?.current?.getMap();
    if (!map) {
        return;
    }
    map.setPaintProperty(`${originalMapObject.uuid}`, "fill-color", originalMapObject.color || "#ff9e00");
};

export const mapObjectVisibility = (context: GlobalStoreContextType, mapObject: MapObject, project: any) => {
    if (mapObject.mappableId === -1) {
        // Newly created map objects that have not been saved yet
        return "visible";
    }
    const mappable = mapObject.getMappable(context);

    if (!mappable) {
        return "none";
    }
    if (context.activeSidebarKey == "deliveries") {
        const eventsForDay = context.calendarEventsByProject
            .flatMap((project) => project.calendarEvents)
            .filter(
                (event) => event.calendarableType === "Delivery" && event.startAt.isSame(context.mapFilterDate, "day")
            );

        return calendarShowArea(context, mappable, eventsForDay) ? "visible" : "none";
    } else if (context.mapFilterDate) {
        if (mappable instanceof Area) {
            const areaOpenStatus = areaOpen(
                mapObject,
                context.mapFilterDate,
                context.mapAreasForProject(project.unique_token),
                context.equipmentForProject(project.unique_token)
            );
            return areaOpenStatus ? "visible" : "none";
        } else if (mappable instanceof Equipment) {
            const equipmentOpenStatus = equipmentOpen(mappable, context.mapFilterDate);
            return equipmentOpenStatus ? "visible" : "none";
        }
    }
    return "visible";
};

export const loadMapLayers = (mapObjects: MapObject[], context: GlobalStoreContextType, layerId: string) => {
    return mapObjects.map((object, idx) => {
        if (!object || !context.mapRef?.current) {
            return null;
        }

        const mappable = object.getMappable(context);

        if (!mappable) {
            return null;
        }

        const visibility = Helper.mapObjectVisibility(context, object);

        switch (object.geometry) {
            case "point":
                return Helper.addIcon(object, layerId, context.mapRef?.current.getMap(), visibility);
            case "lineString":
                return Helper.addLine(object, layerId, visibility);
            case "polygon":
                return Helper.addPolygon(object, layerId, visibility);
            default:
                return null;
        }
    });
};

export const sortMapAreasByType = (a: Area | Equipment, b: Area | Equipment) => {
    // Assign strength based on isDropOff and isWaypoint
    const getStrength = (item: Area | Equipment) => {
        if (item.isDropOff && item.isWaypoint) {
            return 3;
        } else if (item.isDropOff) {
            return 2;
        } else if (item.isWaypoint) {
            return 1;
        } else {
            return 0;
        }
    };

    const aStrength = getStrength(a);
    const bStrength = getStrength(b);

    // Sort by strength first
    if (aStrength !== bStrength) {
        return bStrength - aStrength;
    }

    // Then sort by name
    const aName = a instanceof Area ? a.areaName : (a as Equipment).equipmentName;
    const bName = b instanceof Area ? b.areaName : (b as Equipment).equipmentName;
    return aName.localeCompare(bName);
};
export interface LogisticsDeliveriesByDay {
    [day: string]: {
        deliveryDate: dayjs.Dayjs;
        deliveryTeams: {
            [teamName: string]: {
                team: TeamInfo;
                deliveries: Delivery[];
            };
        };
    };
}

export const layerImagerySource = (url: string) => {
    switch (true) {
        case /dronedeploy.com/i.test(url):
            return "DroneDeploy";
        case /propelleraero.com/i.test(url):
            return "Propeller";
        case /mapbox.com/i.test(url):
            return "Mapbox";
        case /arcgis.com/i.test(url):
            return "ArcGIS";
        case /esri.com/i.test(url):
            return "Esri";
        default:
            return "Other";
    }
};

const bearingToDirectionAndIcon = (b: number) => {
    let direction = "North";
    let icon = faUp;
    if (b > 22.5 && b <= 67.5) {
        direction = "Northeast";
        icon = faUpRight;
    } else if (b > 67.5 && b <= 112.5) {
        direction = "East";
        icon = faRight;
    } else if (b > 112.5 && b <= 157.5) {
        direction = "Southeast";
        icon = faDownRight;
    } else if (b > 157.5 && b <= 202.5) {
        direction = "South";
        icon = faDown;
    } else if (b > 202.5 && b <= 247.5) {
        direction = "Southwest";
        icon = faDownLeft;
    } else if (b > 247.5 && b <= 292.5) {
        direction = "West";
        icon = faLeft;
    } else if (b > 292.5 && b <= 337.5) {
        direction = "Northwest";
        icon = faUpLeft;
    }

    return { direction, icon };
};

export const waypointDirectionsStepsCalc = (waypoint: Area, mapObject: MapObject) => {
    const waypointSteps: any[] = [];
    if (!waypoint || !mapObject) {
        return waypointSteps;
    }

    waypointSteps.push({
        children: (
            <div>
                Enter <span className="font-semibold">{waypoint.areaName}</span> waypoint
            </div>
        ),
        dot: <FontAwesomeIcon icon={faTrafficLight} />,
    });

    // Check if the coordinates are a single array with two numbers (a single point), return early as we do not have steps to reach there
    if (mapObject.geometry == "point") {
        return waypointSteps;
    }

    for (let i = 0; i < mapObject.coordinates.length - 1; i++) {
        const point1 = point(mapObject.coordinates[i]);
        const point2 = point(mapObject.coordinates[i + 1]);
        let unit = "miles";
        let d = turf.distance(point1, point2, { units: "miles" });
        if (d < 0.1) {
            d = d * 5280;
            unit = "feet";
        }
        const b = turf.bearingToAzimuth(turf.bearing(point1, point2));
        const { direction, icon } = bearingToDirectionAndIcon(b);

        waypointSteps.push({
            children: `Head ${direction} for ${d.toFixed(2)} ${unit}`,
            dot: <FontAwesomeIcon icon={icon} />,
        });
    }

    return waypointSteps;
};

export const waypointDirectionApiBuilder = (oldWaypoint: WaypointDirection, newWaypoint: WaypointDirection) => {
    const tempWaypoint = newWaypoint;
    oldWaypoint.waypointSteps?.forEach((step) => {
        if (!newWaypoint.waypointSteps?.find((s) => s.id == step.id)) {
            const stepToDelete = Object.create(step) as WaypointStep;
            stepToDelete._destroy = true;
            tempWaypoint.waypointSteps?.push(stepToDelete);
        }
    });
    return tempWaypoint;
};

export const currentWaypointStepLogic = (currentWaypointStep: WaypointStep | null, step: WaypointStep | null) => {
    if (!currentWaypointStep || !step) {
        return false;
    }
    if (currentWaypointStep.stepNumber === step.stepNumber) {
        return true;
    }
    return false;
};

export const generateWaypointSteps = (area: Area, mapObject: MapObject, timezone: string) => {
    if (mapObject.geometry == "point") {
        return [
            WaypointStep.fromApiObject(
                {
                    step_number: 1,
                    coordinates: mapObject.coordinates,
                    icon: null,
                    instruction: `Arrive at ${area.areaName || ""} waypoint`,
                    color: "#F0F0F0",
                    icon_rotate: 0,
                },
                timezone
            ),
        ];
    }
    return mapObject.coordinates.map((coord: number[], index: number) => {
        let icon = "faMapSigns";
        let instruction = `Proceed to waypoint step ${index + 1}`;
        if (index === 0) {
            icon = "faFlagPennant";
            instruction = `Enter ${area.areaName || ""} waypoint`;
        }
        if (index == mapObject.coordinates.length - 1) {
            icon = "faFlagCheckered";
            instruction = `Arrive at ${area.areaName || ""} waypoint`;
        }

        return WaypointStep.fromApiObject(
            {
                step_number: index + 1,
                coordinates: coord,
                icon: icon,
                instruction: instruction,
                color: "#ff9e00",
                icon_rotate: 0,
            },
            timezone
        );
    });
};

export const mapObjectVisibilityRank = (mapObject: MapObject) => {
    // Should display polygons on bottom, then lines, then points above everything else.
    switch (mapObject.geometry) {
        case "polygon":
            return 0;
        case "lineString":
            return 1;
        case "point":
            return 2;
        default:
            return 3;
    }
};
